import { HttpClient } from "@angular/common/http";
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy
} from "@angular/core";
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { ModalController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { interval, Subscription } from "rxjs";
import { debounce, distinctUntilChanged, filter, tap } from "rxjs/operators";
import { PopupBACSCoefficientComponent } from "src/components/popup/popup-bacs-coefficient/popup-bacs-coefficient.component";
import { PopupEnergyCoefficientComponent } from "src/components/popup/popup-energy-coefficient/popup-energy-coefficient.component";
import { Area } from "src/model/class/general classes/area";
import { Building } from "src/components/shared-components/class/general-classes/building";
import { GroupTimeSlot } from "src/model/class/general classes/group-time-slot";
import { PlantCodeABB } from "src/model/class/general classes/plant-code-abb";
import {
  BACSCoefficient,
  EnergyCoefficient,
} from "src/model/enum/enumDigitalPlace";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { iTranslation } from "src/components/shared-components/interfaces/iTranslation";
import { AreaService } from "src/service/area.service";
import { DpBuildingService } from "src/service/building.service";
import { LoggerService } from "src/service/logger.service";
import { EnvironmentService } from "src/service/environment.service";
import { Environment } from "src/components/shared-components/class/general-classes/environment";
import { currentBuildingChangeSelectionAction } from "src/components/shared-components/state/actions/building.action";
import { Store } from "@ngrx/store";

@Component({
    selector: "app-form-edit-building",
    templateUrl: "./form-edit-building.component.html",
    styleUrls: ["./form-edit-building.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FormEditBuildingComponent
  implements OnInit, OnDestroy, iTranslation {
  private _logContext: string = "form-edit-building.component";
  private _translateSubscription: Subscription = null;
  private _isComponentInitialized: boolean = false;

  private _lblBacsAndTBMHighPerformance: string = "";
  private _lblBacsAndTBMAdvancede: string = "";
  private _lblBacsStandard: string = "";
  private _lblBacsNotEfficient: string = "";
  private _formChangedDebounceTime: number = 500;

  //#region Subcriptions to "_store.select(...)" Observables
  private _selectServerSettingsSub: Subscription = null;
  //#endregion
  public labelEnergyCoefficient: string = "";
  //#endregion

  public formEditBuilding: UntypedFormGroup = null;
  public listGroupTimeSlot: Array<GroupTimeSlot> = null;
  public filterListGroupTimeSlot: Array<GroupTimeSlot> =
    new Array<GroupTimeSlot>();

  public _eventSubscriptions: Array<Subscription> = null;

  public energyCoefficient: number = 0;
  public BACSCoefficient: number = 0;
  public hasCoefficient: boolean = false;
  public hasBACSCoefficient: boolean = false;
  public labelBACSCoefficient: string = "";

  /**
   * Geocoded coordinates of the current address. They are kept on the component
   * (the reactive form has no controls for them) so they survive until save and
   * can be included in the create/update payload.
   */
  public latitude: string = "";
  public longitude: string = "";

  @Input("currentBuilding") currentBuilding: Building;
  @Input() condoEnvironment: Environment;
  @Input("isDemoMode") isDemoMode: boolean = false;
  @Output() reloadOnMap = new EventEmitter<Building>();

  constructor(
    private _fb: UntypedFormBuilder,
    private _translateService: TranslateService,
    private _loggerService: LoggerService,
    private _buildingService: DpBuildingService,
    private _areaService: AreaService,
    private _http: HttpClient,
    private _modalController: ModalController,
    private _environmentService: EnvironmentService,
    private _store: Store
  ) { }

  /**
   * ngOnInit:
   */
  public async ngOnInit(): Promise<void> {
    this.loadTranslation();

    this.subscribeTranslationEvent();
  }

  /**
   * ngOnChanges initialize Form and subscribe if we are in edit mode
   */
  public ngOnChanges() {
    this.initializeForm();
  }

  //#region Translation
  public loadTranslation(): void {
    this._translateService
      .get([
        "dictionary.bacsAndTBMHighPerformance",
        "dictionary.bacsAndTBMAdvanced",
        "dictionary.bacsStandard",
        "dictionary.bacsNotEfficient",
      ])
      .subscribe((translation) => {
        this._lblBacsAndTBMHighPerformance =
          translation["dictionary.bacsAndTBMHighPerformance"];
        this._lblBacsAndTBMAdvancede =
          translation["dictionary.bacsAndTBMAdvanced"];
        this._lblBacsStandard = translation["dictionary.bacsStandard"];
        this._lblBacsNotEfficient = translation["dictionary.bacsNotEfficient"];
        // The form (and its label) is built in ngOnChanges, before these texts arrive
        this.setLabelBACS();
      });
  }

  public subscribeTranslationEvent(): void {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }
  //#endregion

  /**
   * Get form building data merged with the geocoded coordinates.
   *
   * The reactive form does not hold lat/lon controls, so they are merged in here
   * from {@link latitude}/{@link longitude} to make them part of the save payload.
   * @returns The building data to persist, including latitude/longitude.
   */
  public getFormBuildingData(): Building {
    return new Building({
      ...this.formEditBuilding.value,
      latitude: this.latitude,
      longitude: this.longitude,
    });
  }

  /**
   * Open modal to select BACS
   * @param ev
   * @returns
   */
  public async onClickPresentModalBACS(ev: any) {
    const modal = await this._modalController.create({
      component: PopupBACSCoefficientComponent,
      cssClass: "custom-modal-class",
      animated: true,
      backdropDismiss: true,
      componentProps: {
        modalRef: this._modalController,
        BACSSelected: this.BACSCoefficient,
      },
    });

    modal.onDidDismiss().then((dataReturned) => {
      if (dataReturned !== null) {
        if (dataReturned.data.data != undefined) {
          this.formEditBuilding
            .get("BACSCoefficient")
            .setValue(dataReturned.data.data);
          this.hasBACSCoefficient = true;
          this.BACSCoefficient = dataReturned.data.data;
          this.setLabelBACS();
        }
      }
    });

    return await modal.present();
  }

  /**
   * Open modal for select energy coefficient
   * @param ev
   * @returns
   */
  public async onClickPresentModalEnergyCoefficient(ev: any) {
    const modal = await this._modalController.create({
      component: PopupEnergyCoefficientComponent,
      cssClass: "custom-modal-class",
      animated: true,
      backdropDismiss: true,
      componentProps: {
        modalRef: this._modalController,
        chipSelected: this.energyCoefficient,
      },
    });

    modal.onDidDismiss().then((dataReturned) => {
      if (dataReturned !== null) {
        if (dataReturned.data.data != undefined) {
          this.formEditBuilding
            .get("energyCoefficient")
            .setValue(dataReturned.data.data);

          this.hasCoefficient = true;

          this.energyCoefficient = dataReturned.data.data;
          this.labelEnergyCoefficient =
            EnergyCoefficient[this.energyCoefficient];
        }
      }
    });

    return await modal.present();
  }

  /**
   * ngOnDestroy
   */
  public ngOnDestroy(): void {
    this._translateSubscription?.unsubscribe();

    this.unsubscribeStoreSelect();
  }

  /**
   * Get class for chip of energy coefficient
   * @returns
   */
  public getClassChip() {
    let classChip: string = "leftChip ";

    if (this.energyCoefficient != 0 && this.energyCoefficient != undefined) {
      if (
        this.energyCoefficient != EnergyCoefficient["A4"] &&
        this.energyCoefficient != EnergyCoefficient["A3"] &&
        this.energyCoefficient != EnergyCoefficient["A2"] &&
        this.energyCoefficient != EnergyCoefficient["A1"]
      ) {
        classChip +=
          " ion-chip-" +
          EnergyCoefficient[this.energyCoefficient].toLowerCase();
      } else {
        if (this.energyCoefficient == EnergyCoefficient["A4"]) {
          classChip += " ion-chip-appp";
        } else if (this.energyCoefficient == EnergyCoefficient["A3"]) {
          classChip += " ion-chip-app";
        } else if (this.energyCoefficient == EnergyCoefficient["A2"]) {
          classChip += " ion-chip-ap";
        } else if (this.energyCoefficient == EnergyCoefficient["A1"]) {
          classChip += " ion-chip-a";
        }
      }
    }
    return classChip;
  }

  /**
   * Get class fro badge of bacs
   * @returns
   */
  public getClassChipBACS() {
    let classChip: string = "leftChip ";

    if (this.BACSCoefficient != 0 && this.BACSCoefficient != undefined) {
      classChip +=
        " ion-chip-bacs-" + BACSCoefficient[this.BACSCoefficient].toLowerCase();
    }
    return classChip;
  }

  /**
   * Set label for BACS
   */
  private setLabelBACS() {
    if (!this.BACSCoefficient) {
      return;
    }

    // Description of the class currently selected (not the saved one), so a fresh pick, a
    // new building and an existing one all show "<class> - <description>"
    const descriptions: { [key: number]: string } = {
      [BACSCoefficient.A]: this._lblBacsAndTBMHighPerformance,
      [BACSCoefficient.B]: this._lblBacsAndTBMAdvancede,
      [BACSCoefficient.C]: this._lblBacsStandard,
      [BACSCoefficient.D]: this._lblBacsNotEfficient,
    };
    const description = descriptions[this.BACSCoefficient];

    this.labelBACSCoefficient = BACSCoefficient[this.BACSCoefficient];
    if (description) {
      this.labelBACSCoefficient += " - " + description;
    }
  }

  /**
   * Initialize form
   */
  private initializeForm(): void {
    this.formEditBuilding = this._fb.group(
      {
        name: new UntypedFormControl(this.currentBuilding?.name),
        address: new UntypedFormControl(this.currentBuilding?.address),
        city: new UntypedFormControl(this.currentBuilding?.city),
        province: new UntypedFormControl(this.currentBuilding?.province),
        taxCode: new UntypedFormControl(this.currentBuilding?.taxCode),
        energyCoefficient: new UntypedFormControl(
          this.currentBuilding?.energyCoefficient
        ),
        BACSCoefficient: new UntypedFormControl(this.currentBuilding?.BACSCoefficient),
      },
      {
        updateOn: "change",
      }
    );

    this.latitude = this.currentBuilding?.latitude ?? "";
    this.longitude = this.currentBuilding?.longitude ?? "";

    this.energyCoefficient = this.currentBuilding?.energyCoefficient;
    this.labelEnergyCoefficient = EnergyCoefficient[this.energyCoefficient];
    if (this.currentBuilding?.energyCoefficient) {
      this.hasCoefficient = true;
    }

    this.BACSCoefficient = this.currentBuilding?.BACSCoefficient;
    this.labelBACSCoefficient = BACSCoefficient[this.BACSCoefficient];
    if (this.currentBuilding?.BACSCoefficient) {
      this.hasBACSCoefficient = true;
    }
    this.setLabelBACS();
    if (this.isDemoMode) {
      this.formEditBuilding.disable();
    }
  }

  /**
   * Geocode the address currently entered in the form (address + city + province)
   * into {@link latitude}/{@link longitude} using the OpenStreetMap Nominatim API.
   *
   * The result is stored on the component so it can be included in the save payload
   * (the reactive form has no lat/lon controls). Network/parsing failures are caught
   * and logged: the previously known coordinates are preserved so a transient
   * geocoding error never wipes a valid location.
   *
   * @param clearOnEmpty When `true`, an empty address or a "no results" response
   * resets the coordinates to empty (used by the live map preview). When `false`
   * the existing coordinates are kept (used on save, to avoid data loss).
   * @returns A promise that resolves once geocoding has completed.
   */
  public async geocodeCurrentAddress(clearOnEmpty: boolean = false): Promise<void> {
    const formValue: Building = this.formEditBuilding.value;
    const query = [formValue.address, formValue.city, formValue.province]
      .filter((part) => !!part && `${part}`.trim().length > 0)
      .join(" ")
      .trim();

    if (!query) {
      if (clearOnEmpty) {
        this.latitude = "";
        this.longitude = "";
      }
      return;
    }

    const geocodeUrl =
      "https://nominatim.openstreetmap.org/search?q=" +
      encodeURIComponent(query) +
      "&format=json&limit=1";

    try {
      const data: any = await this._http.get(geocodeUrl).toPromise();
      if (data && data[0]) {
        this.latitude = data[0].lat;
        this.longitude = data[0].lon;
      } else if (clearOnEmpty) {
        this.latitude = "";
        this.longitude = "";
      }
    } catch (exception) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "geocodeCurrentAddress",
        `Geocoding failed for query "${query}": ${exception?.message ?? exception}`
      );
    }
  }

  /**
   * Recalculate latitude and longitude on address/city/province blur and refresh
   * the map preview with the freshly geocoded coordinates.
   */
  public async recalculateLatLon(): Promise<void> {
    await this.geocodeCurrentAddress(true);

    const buildingPreview: Building = new Building({
      ...this.formEditBuilding.value,
      latitude: this.latitude,
      longitude: this.longitude,
    });

    this.reloadOnMap.emit(buildingPreview);
  }

  /**
   * Set data form values
   */
  public updateFormValues(): void {
    this.formEditBuilding?.patchValue(
      {
        name: this.currentBuilding.name,
        address: this.currentBuilding.address,
        city: this.currentBuilding.city,
        province: this.currentBuilding.province,
        taxCode: this.currentBuilding.taxCode,
        energyCoefficient: this.currentBuilding.energyCoefficient,
        BACSCoefficient: this.currentBuilding.BACSCoefficient,
      },
      { onlySelf: true, emitEvent: false }
    );
  }

  //#region Unsubscribe from "_store.selec(...)" Observable

  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    this._selectServerSettingsSub?.unsubscribe();
  }

  //#endregion
}
